import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { caseCategories, cases } from "@/mocks/cases";

interface FeaturedCasesProps {
  activeCategory: string;
  onChangeCategory: (id: string) => void;
  onOpenCase: (id: string) => void;
}

export default function FeaturedCases({ activeCategory, onChangeCategory, onOpenCase }: FeaturedCasesProps) {
  const list = activeCategory === "all" ? cases : cases.filter((item) => item.category === activeCategory);
  const currentLabel =
    caseCategories.find((cat) => cat.id === activeCategory)?.name ?? "全部现场";

  return (
    <section id="featured" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="01"
          title="热门翻车现场 · 本期精选"
          subtitle="按现场类型挑选最具代表性的案例。点开任意一份档案，可以看到完整的关键节点回放与结论。"
        />

        <div className="no-scrollbar -mx-4 flex overflow-x-auto px-4 pb-1 md:mx-0 md:px-0">
          <div className="inline-flex shrink-0 rounded-full border border-background-200 bg-background-100 px-1 py-1">
            {caseCategories.map((cat) => {
              const active = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onChangeCategory(cat.id)}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs transition-colors md:px-4 md:text-sm ${
                    active
                      ? "bg-primary-600 font-semibold text-background-50"
                      : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`} />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mt-5 text-xs text-foreground-600 md:text-sm">
          当前筛选：<span className="font-semibold text-primary-700">{currentLabel}</span>
          <span className="mx-2 text-background-300">|</span>
          共 {list.length} 份档案
        </p>

        {list.length === 0 ? (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-folder-open-line" />
            </span>
            <p className="mt-4 text-sm text-foreground-600">这一类现场暂时还没归档，换个分类看看？</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {list.map((item, index) => {
              const isLead = index === 0;
              return (
                <Reveal
                  key={item.id}
                  delay={index * 60}
                  className={isLead ? "md:col-span-2 lg:col-span-2" : ""}
                >
                  {isLead ? (
                    <article className="group relative h-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
                      <div className="h-64 w-full md:h-80">
                        <img
                          src={item.cover}
                          alt={`${item.categoryName}案例配图：${item.title}`}
                          title={`${item.title} · 曝光档案局`}
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/55 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-semibold text-accent-950">
                            <i className="ri-flashlight-fill" />
                            本期头条
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-background-50/30 px-3 py-1 text-[11px] text-background-100">
                            {item.categoryName}
                          </span>
                        </div>
                        <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-background-50 md:text-2xl">
                          {item.title}
                        </h3>
                        <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                          {item.summary}
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300">
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-eye-line" />
                            {item.views}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-heart-3-line" />
                            {item.likes}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-time-line" />
                            {item.duration}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onOpenCase(item.id)}
                        aria-label={`查看档案：${item.title}`}
                        className="absolute inset-0 cursor-pointer"
                      />
                    </article>
                  ) : (
                    <article className="group relative flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                      <div className="h-44 w-full overflow-hidden">
                        <img
                          src={item.cover}
                          alt={`${item.categoryName}案例配图：${item.title}`}
                          title={`${item.title} · 曝光档案局`}
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-4 md:p-5">
                        <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                          <i className="ri-price-tag-3-line" />
                          {item.categoryName}
                        </span>
                        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </h3>
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                          {item.summary}
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-3 text-[11px] text-foreground-500">
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-eye-line" />
                            {item.views}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <i className="ri-heart-3-line" />
                            {item.likes}
                          </span>
                          <span className="ml-auto inline-flex items-center gap-1 font-medium text-primary-700">
                            查看档案
                            <i className="ri-arrow-right-line" />
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onOpenCase(item.id)}
                        aria-label={`查看档案：${item.title}`}
                        className="absolute inset-0 cursor-pointer"
                      />
                    </article>
                  )}
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}